import { Component, Inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import Swal from 'sweetalert2';
import { Paciente } from '../../pacientes/pacientes';
import { PacienteService } from '../../../core/services/pacientes.service';
import { VALOR_EXPEDIENTE_VACIO } from '../../../core/constants/expendiente';

export interface CampoHistoria {
  control: string;
  etiqueta: string;
  placeholder?: string;
}

export const GRUPOS_HISTORIA: { titulo: string; icono: string; campos: CampoHistoria[] }[] = [
  {
    titulo: 'Antecedentes personales no patológicos',
    icono: 'self_improvement',
    campos: [
      { control: 'vacunas', etiqueta: 'Vacunas' },
      { control: 'viajesRecientes', etiqueta: 'Viajes recientes' },
      { control: 'lugarOrigen', etiqueta: 'Lugar de origen y de residencia' },
      { control: 'alimentacion', etiqueta: 'Alimentación' },
      { control: 'agua', etiqueta: 'Agua' },
      {
        control: 'vivienda',
        etiqueta: 'Vivienda',
        placeholder: 'Tipo de vivienda (renta, propia, prestada) y descripción general',
      },
    ],
  },
  {
    titulo: 'Antecedentes personales patológicos',
    icono: 'medical_information',
    campos: [
      { control: 'alergias', etiqueta: 'Alergias' },
      { control: 'transfucionesSangre', etiqueta: 'Transfusiones de sangre' },
      { control: 'cirugiasPrevias', etiqueta: 'Cirugías previas' },
      { control: 'traumasFracturasPrev', etiqueta: 'Traumas o fracturas previas' },
      { control: 'enfermedadesConcomitantes', etiqueta: 'Enfermedades concomitantes' },
      { control: 'diabetes', etiqueta: 'Diabetes' },
      { control: 'hipertension', etiqueta: 'Hipertensión' },
      { control: 'asma', etiqueta: 'Asma' },
      { control: 'epoc', etiqueta: 'Enfermedad pulmonar obstructiva crónica' },
      { control: 'antecedenteCancer', etiqueta: 'Antecedentes de cáncer en familia' },
      { control: 'antecedentesTuberculosis', etiqueta: 'Antecedentes de tuberculosis' },
    ],
  },
];

@Component({
  selector: 'app-historia-clinica-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
  ],
  templateUrl: './historia-clinica-dialog.html',
  styleUrl: './historia-clinica-dialog.scss',
})
export class HistoriaClinicaDialog {
  readonly grupos = GRUPOS_HISTORIA;
  private readonly campos = GRUPOS_HISTORIA.flatMap((g) => g.campos);

  form = new FormGroup(
    Object.fromEntries(
      this.campos.map((c) => [
        c.control,
        new FormControl('', { nonNullable: true, validators: Validators.required }),
      ]),
    ) as Record<string, FormControl<string>>,
  );

  guardando = false;
  editable: boolean;

  constructor(
    private dialogRef: MatDialogRef<HistoriaClinicaDialog>,
    private pacienteService: PacienteService,
    @Inject(MAT_DIALOG_DATA)
    public data: { paciente: Paciente; editable: boolean },
  ) {
    this.editable = data.editable;

    const expediente: any = data.paciente?.expediente ?? {};
    const valores: Record<string, string> = {};

    for (const campo of this.campos) {
      const valor = expediente[campo.control] ?? '';
      valores[campo.control] = valor === VALOR_EXPEDIENTE_VACIO ? '' : valor;
    }

    this.form.patchValue(valores);

    // Con la consulta finalizada queda en solo lectura
    if (!this.editable) {
      this.form.disable();
    }
  }

  async cerrar(): Promise<void> {
    if (this.form.dirty && !this.guardando) {
      const r = await Swal.fire({
        title: '¿Descartar cambios?',
        text: 'Hay cambios sin guardar en la historia clínica.',
        icon: 'warning',
        showCancelButton: true,

        confirmButtonText: 'Sí, descartar',

        cancelButtonText: 'Seguir editando',

        confirmButtonColor: '#d33',
      });

      if (!r.isConfirmed) {
        return;
      }
    }

    this.dialogRef.close();
  }

  guardar(): void {
    if (!this.editable) {
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();

      const primero = this.campos.find((c) => this.form.get(c.control)?.invalid);

      setTimeout(() => {
        const elemento = document.getElementById(`historia-${primero?.control}`);
        elemento?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        elemento?.focus();
      }, 100);

      Swal.fire({
        icon: 'warning',
        title: 'Historia clínica incompleta',
        text: 'Completa los campos obligatorios de la historia clínica.',
        showConfirmButton: false,
        timer: 2000,
      });
      return;
    }

    const expediente: Record<string, string> = {};

    for (const campo of this.campos) {
      expediente[campo.control] = (this.form.get(campo.control)?.value ?? '').trim();
    }

    this.guardando = true;

    this.pacienteService.actualizarPaciente(this.data.paciente.id, { expediente }).subscribe({
      next: () => {
        this.guardando = false;
        this.form.markAsPristine();

        // El padre actualiza paciente.expediente con esto
        this.dialogRef.close(expediente);

        Swal.fire({
          icon: 'success',
          title: 'Historia clínica guardada',
          text: 'Los antecedentes del paciente se guardaron correctamente.',
          showConfirmButton: false,
          timer: 2000,
        });
      },

      error: (error) => {
        this.guardando = false;

        Swal.fire({
          icon: 'error',
          title: 'No se pudo guardar',
          text:
            error?.error?.message?.join?.(', ') ??
            error?.error?.message ??
            'Ocurrió un error al guardar la historia clínica.',
          showConfirmButton: false,
          timer: 2000,
        });
      },
    });
  }
}
